import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Typography,
  Divider,
  Card,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MDBox from "components/MDBox"; // Adjust path to your MDBox
import ScorecardView from "layouts/user/KPIs/myScorecard/components/ScorecardView";
import KPIHistoryChart from "layouts/user/KPIs/myScorecard/components/KPIHistoryView";
import { getEmployees } from "services/api";
import { useTranslation } from "react-i18next";

// Import your API service to get employees
// import { getAllEmployees } from "services/api";

const UserScorecardsView = () => {
  const { t } = useTranslation();
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subView, setSubView] = useState("scorecard"); // 'scorecard' | 'history'
  // 1. Fetch Employees on Mount
  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        // Replace this with your actual API call
        // const response = await getAllEmployees();
        // setAllEmployees(response);

        // Mock data example (Delete this when connecting API)
        const response = await getEmployees();
        if (response != "error") setAllEmployees(response);
      } catch (error) {
        console.error("Error fetching employees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployee) setSubView("scorecard");
  }, [selectedEmployee]);

  return (
    <MDBox pt={2} pb={3}>
      {/* 2. Employee Selector Section */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="medium" mb={2}>
          {t("user.myTeam.selectEmp")}
        </Typography>
        <Autocomplete
          options={allEmployees}
          getOptionLabel={(option) => option.name}
          value={selectedEmployee}
          onChange={(event, newValue) => setSelectedEmployee(newValue)}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.dashboard.employee")}
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Card>

      {/* 3. Render Views if Employee Selected */}
      {selectedEmployee ? (
        <MDBox>
          {/* --- HEADER: Name + Switch --- */}
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            pl={1}
            flexWrap="wrap" // Ensures responsiveness on small screens
            gap={2}
          >
            <Typography variant="h5" fontWeight="bold">
              {/* {subView === "scorecard" ? "Scorecard Overview" : "KPI History Analysis"}:{" "} */}
              {subView === "scorecard"
                ? t("user.myTeam.scorecardOverview")
                : t("user.myTeam.kpiHistory")}{" "}
              : {selectedEmployee.name}
            </Typography>

            <ToggleButtonGroup
              value={subView}
              exclusive
              onChange={(event, newView) => {
                if (newView !== null) setSubView(newView);
              }}
              size="small"
              color="info" // Use 'info' or 'primary' based on your theme
              sx={{ bgcolor: "background.paper" }}
            >
              <ToggleButton value="scorecard" sx={{ px: 3, fontWeight: "bold" }}>
                {t("user.myTeam.scorecard")}
              </ToggleButton>
              <ToggleButton value="history" sx={{ px: 3, fontWeight: "bold" }}>
                {t("user.myTeam.kpiHistory")}
              </ToggleButton>
            </ToggleButtonGroup>
          </MDBox>

          {/* --- CONDITIONAL CONTENT --- */}
          {subView === "scorecard" ? (
            <MDBox>
              <ScorecardView employee={selectedEmployee} isMe={false} isAdminView={true} />
            </MDBox>
          ) : (
            <MDBox>
              <KPIHistoryChart employee={selectedEmployee} isMe={false} />
            </MDBox>
          )}
        </MDBox>
      ) : (
        /* Empty State */
        <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography variant="body1" color="textSecondary">
            {t("user.myTeam.pleaseSelectEmployee")}
          </Typography>
        </MDBox>
      )}
    </MDBox>
  );
};

export default UserScorecardsView;
